export interface SheetDetents {
  compact: number;
  expanded: number;
}

export interface MobileSheetOptions {
  root: HTMLElement;
  header: HTMLElement;
  getExpanded: () => boolean;
  setExpanded: (expanded: boolean) => void;
  measureDetents: () => SheetDetents;
  prefersReducedMotion?: () => boolean;
  requestFrame?: (callback: FrameRequestCallback) => number;
  cancelFrame?: (handle: number) => void;
}

export interface MobileSheetController {
  destroy: () => void;
  isDragging: () => boolean;
  setExpanded: (expanded: boolean) => void;
}

const INTENT_DISTANCE_PX = 6;
const FLICK_VELOCITY_PX_MS = 0.5;
const VELOCITY_WINDOW_MS = 100;
const MAX_RUBBER_BAND_PX = 80;

function rubberBand(value: number, { compact, expanded }: SheetDetents): number {
  if (value < compact) {
    const maximum = Math.min(MAX_RUBBER_BAND_PX, compact * 0.8);
    const distance = compact - value;
    return compact - maximum * (1 - Math.exp(-distance / Math.max(maximum, 1)));
  }
  if (value > expanded) {
    const distance = value - expanded;
    return expanded + MAX_RUBBER_BAND_PX * (1 - Math.exp(-distance / MAX_RUBBER_BAND_PX));
  }
  return value;
}

/** Attach a two-detent, header-only Pointer Events gesture to a mobile sheet. */
export function attachMobileSheet(options: MobileSheetOptions): MobileSheetController {
  const { root, header, getExpanded, setExpanded: applyExpanded, measureDetents } = options;
  const requestFrame = options.requestFrame ?? (
    typeof requestAnimationFrame === 'function'
      ? requestAnimationFrame
      : (callback: FrameRequestCallback) => window.setTimeout(() => callback(performance.now()), 0)
  );
  const cancelFrame = options.cancelFrame ?? (
    typeof cancelAnimationFrame === 'function' ? cancelAnimationFrame : window.clearTimeout
  );
  const prefersReducedMotion = options.prefersReducedMotion ?? (() =>
    typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  let pointerId: number | null = null;
  let startX = 0;
  let startY = 0;
  let startHeight = 0;
  let currentHeight = 0;
  let samples: Array<{ y: number; time: number }> = [];
  let detents: SheetDetents = { compact: 0, expanded: 0 };
  let dragging = false;
  let horizontalIntent = false;
  let suppressClick = false;
  let frame: number | null = null;
  let pendingHeight: number | null = null;
  let settleTimer: ReturnType<typeof setTimeout> | null = null;
  let settleGeneration = 0;

  const clearSettledHeight = (generation = settleGeneration): void => {
    if (generation !== settleGeneration) return;
    if (settleTimer !== null) clearTimeout(settleTimer);
    settleTimer = null;
    root.classList.remove('vitals--settling');
    root.style.removeProperty('--vitals-sheet-height');
  };

  const renderPendingHeight = (): void => {
    frame = null;
    if (pendingHeight === null) return;
    root.style.setProperty('--vitals-sheet-height', `${pendingHeight}px`);
    pendingHeight = null;
  };

  const renderHeight = (height: number): void => {
    pendingHeight = height;
    if (frame === null) frame = requestFrame(renderPendingHeight);
  };

  const settle = (expanded: boolean): void => {
    settleGeneration += 1;
    const generation = settleGeneration;
    if (settleTimer !== null) clearTimeout(settleTimer);
    settleTimer = null;
    if (frame !== null) {
      cancelFrame(frame);
      frame = null;
    }
    pendingHeight = null;
    dragging = false;
    root.classList.remove('vitals--dragging');
    root.style.setProperty('--vitals-sheet-height', `${currentHeight}px`);
    // Establish the snap's starting height before the settled class reflows the
    // contents and the next animation frame moves the sheet to its detent.
    root.getBoundingClientRect();
    root.classList.add('vitals--settling');
    applyExpanded(expanded);
    if (prefersReducedMotion()) {
      clearSettledHeight(generation);
      return;
    }
    renderHeight(expanded ? detents.expanded : detents.compact);
    settleTimer = setTimeout(() => clearSettledHeight(generation), 300);
  };

  const releaseCapture = (): void => {
    if (pointerId === null) return;
    try {
      header.releasePointerCapture?.(pointerId);
    } catch {
      // Capture may already have been released by the browser.
    }
  };

  const resetPointer = (): void => {
    pointerId = null;
    dragging = false;
    horizontalIntent = false;
    root.classList.remove('vitals--dragging');
  };

  const onPointerDown = (event: PointerEvent): void => {
    if (pointerId !== null || event.button !== 0) return;
    suppressClick = false;
    pointerId = event.pointerId;
    startX = event.clientX;
    startY = event.clientY;
    samples = [{ y: event.clientY, time: event.timeStamp }];
    horizontalIntent = false;
    const interruptedHeight = root.classList.contains('vitals--settling')
      ? root.getBoundingClientRect().height
      : null;
    if (settleTimer !== null) clearTimeout(settleTimer);
    settleTimer = null;
    detents = measureDetents();
    startHeight = interruptedHeight ?? (getExpanded() ? detents.expanded : detents.compact);
    currentHeight = startHeight;
    if (interruptedHeight !== null) {
      root.style.setProperty('--vitals-sheet-height', `${interruptedHeight}px`);
    }
  };

  const onPointerMove = (event: PointerEvent): void => {
    if (event.pointerId !== pointerId || horizontalIntent) return;
    const dx = event.clientX - startX;
    const dy = event.clientY - startY;
    if (!dragging) {
      if (Math.hypot(dx, dy) < INTENT_DISTANCE_PX) return;
      if (Math.abs(dx) >= Math.abs(dy)) {
        horizontalIntent = true;
        return;
      }
      dragging = true;
      suppressClick = true;
      root.classList.remove('vitals--settling');
      root.classList.add('vitals--dragging');
      header.setPointerCapture?.(event.pointerId);
    }

    event.preventDefault();
    samples.push({ y: event.clientY, time: event.timeStamp });
    samples = samples.filter((sample) => event.timeStamp - sample.time <= VELOCITY_WINDOW_MS);
    currentHeight = rubberBand(startHeight - dy, detents);
    renderHeight(currentHeight);
  };

  const finish = (event: PointerEvent, cancelled: boolean): void => {
    if (event.pointerId !== pointerId) return;
    if (!dragging) {
      resetPointer();
      return;
    }
    event.preventDefault();
    releaseCapture();
    let velocity = 0;
    if (!cancelled) {
      const dy = event.clientY - startY;
      currentHeight = rubberBand(startHeight - dy, detents);
      samples.push({ y: event.clientY, time: event.timeStamp });
      samples = samples.filter((sample) => event.timeStamp - sample.time <= VELOCITY_WINDOW_MS);
      const firstSample = samples[0];
      const lastSample = samples.at(-1);
      const sampleDuration = firstSample && lastSample ? lastSample.time - firstSample.time : 0;
      velocity = firstSample && lastSample && sampleDuration > 0
        ? -(lastSample.y - firstSample.y) / sampleDuration
        : 0;
    }
    const midpoint = (detents.compact + detents.expanded) / 2;
    const expand = cancelled || Math.abs(velocity) < FLICK_VELOCITY_PX_MS
      ? currentHeight >= midpoint
      : velocity > 0;
    if (cancelled) suppressClick = false;
    settle(expand);
    resetPointer();
  };

  const onPointerUp = (event: PointerEvent): void => finish(event, false);
  const onPointerCancel = (event: PointerEvent): void => finish(event, true);
  const onClick = (event: MouseEvent): void => {
    if (!suppressClick) return;
    suppressClick = false;
    event.preventDefault();
    event.stopImmediatePropagation();
  };
  header.addEventListener('pointerdown', onPointerDown);
  header.addEventListener('pointermove', onPointerMove);
  header.addEventListener('pointerup', onPointerUp);
  header.addEventListener('pointercancel', onPointerCancel);
  header.addEventListener('click', onClick, true);

  return {
    destroy: () => {
      if (frame !== null) cancelFrame(frame);
      frame = null;
      pendingHeight = null;
      if (settleTimer !== null) clearTimeout(settleTimer);
      settleTimer = null;
      releaseCapture();
      resetPointer();
      clearSettledHeight();
      header.removeEventListener('pointerdown', onPointerDown);
      header.removeEventListener('pointermove', onPointerMove);
      header.removeEventListener('pointerup', onPointerUp);
      header.removeEventListener('pointercancel', onPointerCancel);
      header.removeEventListener('click', onClick, true);
    },
    isDragging: () => dragging,
    setExpanded: (expanded) => {
      const explicitHeight = Number.parseFloat(
        root.style.getPropertyValue('--vitals-sheet-height'),
      );
      const renderedHeight = Number.isFinite(explicitHeight)
        ? root.getBoundingClientRect().height || explicitHeight
        : null;
      detents = measureDetents();
      currentHeight = renderedHeight ?? (getExpanded() ? detents.expanded : detents.compact);
      settle(expanded);
    },
  };
}
